import {SetStateAction, useMemo, useState} from "react";
import {Key} from "react-aria-components";

type UseSelectOptions = {
	defaultItem?: Key;
};

type UseSelect = [
	selectedItem: Key,
	{
		setSelectedItem: React.Dispatch<SetStateAction<Key>>;
		onSelectionChange: (key: Key) => void;
	},
];

/**
 * useSelect custom hook can be coupled with a Select component to control the
 * selection state and perform side effects.
 * @param options setting a defaultItem will initialise the state with its value.
 *                passing an onSelectionChange callback will trigger the function
 *                when the selection changes.
 * @returns a tuple of [selectedItem, handlers] which contain the key and state
 *          management functions respectively.
 */
const useSelect: (options?: UseSelectOptions) => UseSelect = ({
	defaultItem = "",
} = {}) => {
	const [selectedItem, setSelectedItem] = useState<Key>(defaultItem);

	const handlers = useMemo(
		() => ({
			setSelectedItem,
			onSelectionChange: (selection: Key) => {
				setSelectedItem(selection);
			},
		}),
		[]
	);

	return [selectedItem, handlers];
};

export default useSelect;
